/**
 * Franchise deposit flows of one seat and product (`depositFlow.v1@1`): franchise attrition (02.3 E1–E2). The market's growth, the money-fund pool (E1a),
 * checking migration (02.2, `dda-migration.ts`), the logit transfer and hot money are the engine's 02.1 step, not here.
 *
 * Conventions where the port is silent:
 * - `balanceUsd` is the seat's branch-served balance BD (02.3; the caller scales the branch-scope balances by
 *   `branchServedShareRate`, ADR-0031) on the attrition call, and B_dda on the DDA call (02.2 E2).
 * - `branchCountChange` is the change as a fraction of the SimStart branch count (L15 rate: −0.2 closes 20%), so the cumulative
 *   closure depth is x1 = x0 − change.
 */
import { InvariantViolation, roundToUsd, type DepositFlowModel, type DepositFlowParams, type FranchiseState, type Usd } from "@bsim/domain";
import { pinOf, type Pins } from "./pins";

/**
 * 02.1 E1a (ADR-0055, R5): the share of deposits the money-fund pool holds at a rate gap, p* = ℓ_p·(G − G₀) ÷ 0.01, G = fed funds less the deposit market's average
 * rate and G₀ SimStart's; negative when the gap is below SimStart's (money comes into the banks). It closes when the gap closes.
 */
export const moneyFundPoolTargetRate = (p: NonNullable<DepositFlowParams["moneyFundPool"]>, gap: number, baselineGap: number): number =>
  (p.shareOfDepositsPer100BpGap * (gap - baselineGap)) / 0.01;

type PortInput = Parameters<DepositFlowModel["monthlyFlow"]>[1];

const required = (v: number | undefined, field: string): number => {
  if (v === undefined) throw new InvariantViolation("I19", `depositFlow param ${field} missing`);
  return v;
};

/** 02.3 E2: h = 1 − (1 − ρ)^(1/N), the monthly hazard that realizes ρ of a pending stock within N months. */
const hazard = (realizedShare: number, months: number): number => 1 - Math.pow(1 - realizedShare, 1 / months);

/** 02.3 E3: months an opened branch takes to reach its mature deposits (pin `flow.branchOpening.maturityMonths`); absent = openings count in full at once (the engine then books them straight into the stock). */
export const branchOpeningMaturityMonths = (pins: Pins): number | undefined => pinOf(pins, "flow.branchOpening.maturityMonths");

/**
 * 02.3 E3: the franchise gain in `month` from branches opened and not yet mature: each opening adds ω_open·a_br·(count ÷ n₀)·BD ÷ N to Φ in each of its N months
 * (a linear ramp, the cumulative gain reaching ω_open·a_br·(count ÷ n₀)·BD at maturity; BD read at the month, so the ramp scales with the bank). Positive = a gain.
 */
export function openingGainUsd(p: DepositFlowParams, openings: FranchiseState["pendingOpenings"], month: number, branchCountAtSimStart: number, branchDepositsUsd: number, maturityMonths: number): number {
  let gain = 0;
  for (const { openedMonth, count } of openings ?? []) {
    const age = month - openedMonth;
    if (age >= 0 && age < maturityMonths) gain += count;
  }
  return gain === 0 ? 0 : (required(p.branchOpeningGainShare, "branchOpeningGainShare") * p.branchClosureAttritionRate * (gain / branchCountAtSimStart) * branchDepositsUsd) / maturityMonths;
}

/** 02.3 E1: pending added in the decision's first month; negative = a gain (service points gained). Openings are E3's ramp, not a pending stock. */
function pendingAdds(p: DepositFlowParams, input: PortInput): { branch: number; service: number } {
  const { balanceUsd: bd, branchCountChange: change, closureDepthBefore: x0, serviceIndexDelta: dq } = input;
  const aBr = p.branchClosureAttritionRate;
  let branch = 0;
  if (change < 0) {
    const x1 = x0 - change;
    const a = (aBr * (x0 + x1)) / required(p.branchClosureAttritionRefCutRate, "branchClosureAttritionRefCutRate"); // depth rule
    branch = a * -change * bd;
  }
  const { ratePerPointOfBranchDeposits: aSvc, gainShareOfLoss: omega } = p.serviceAttrition;
  const service = aSvc * bd * (dq < 0 ? -dq : -omega * dq);
  return { branch, service };
}

export const depositFlowV1: DepositFlowModel = {
  kind: "depositFlow",
  monthlyFlow: (call, input) => {
    const p = call.ref.params;
    const adds = pendingAdds(p, input);
    const realized = (pending: number, months: number): number =>
      pending === 0 ? 0 : -pending * hazard(required(p.attritionRealizedShare, "attritionRealizedShare"), months); // 02.3 E2: P −= A; loss = negative flow
    return {
      franchiseFlowUsd: {
        ddaMigration: 0 as Usd, // retired (ADR-0055): checking migration is a level, drawn by the engine's deposit step (02.2 E3)
        branchAttrition: roundToUsd(realized(input.closedBranchDepositsPendingUsd, p.branchAttritionRealizationMonths)),
        serviceAttrition: roundToUsd(realized(input.serviceAttritionPendingUsd, p.serviceAttrition.realizationMonths)),
      },
      pendingAddUsd: { branch: roundToUsd(adds.branch), service: roundToUsd(adds.service) },
    };
  },
};

/**
 * 02.5 / 02.6 (Gate 3c; ADR-0048): the front-book rules of one deposit product, read off its `depositFlow` params. A product with no
 * `newMoneyShareByProduct` entry has no front lots (the one-rate market of 02.1: dda); a CD takes the acquisition and sensitivity only.
 * `monthlyTurnoverRate` = τ ÷ 12, `acquisitionPerMonthRate` = a ÷ 12. Gaps are bp, balances whole or fractional dollars.
 */
export interface FrontBookRules {
  /** Whether the product keeps front lots and a second (base) rate: checking and the money markets; a CD has one rate and no lots. */
  readonly frontLots: boolean;
  readonly sensitivity: number;
  readonly monthlyTurnoverRate: number;
  readonly acquisitionPerMonthRate: number;
  readonly elasticity: number;
  readonly ratePerNotch: number;
  readonly notchBp: number;
  readonly ownBaseShare: number;
  readonly frontMonths: number;
  readonly leakPerMonthRate: number;
  readonly leakNewMoneyGapShare: number;
  readonly lotEveDurationYears: number;
  readonly lotBeta: number;
  /** 02.5 E6 (P4-D2): the share of the product's franchise that is operating money and never leaks for rate; 0 = no floor. */
  readonly operatingFloorShare: number;
}

/** The rules of `productId` (absent when the params carry no front-book keys or the product is not rate-sensitive). */
export function frontBookRules(p: DepositFlowParams, productId: string): FrontBookRules | undefined {
  const sensitivity = p.rateSensitivityMultByProduct?.[productId as keyof typeof p.rateSensitivityMultByProduct];
  if (sensitivity === undefined) return undefined;
  const turnover = p.newMoneyShareByProduct?.[productId as keyof typeof p.newMoneyShareByProduct];
  return {
    frontLots: turnover !== undefined, sensitivity,
    monthlyTurnoverRate: (turnover ?? 0) / 12,
    acquisitionPerMonthRate: required(p.acquisitionBaseRatePerYear, "acquisitionBaseRatePerYear") / 12,
    elasticity: required(p.marketingAcquisitionElasticity, "marketingAcquisitionElasticity"),
    ratePerNotch: required(p.acquisitionRatePerNotch, "acquisitionRatePerNotch"),
    notchBp: required(p.newMoneyNotchBp, "newMoneyNotchBp"),
    ownBaseShare: required(p.ownBaseMigrationShare, "ownBaseMigrationShare"),
    frontMonths: required(p.frontBookMonths, "frontBookMonths"),
    leakPerMonthRate: required(p.baseLeakRatePerMonth, "baseLeakRatePerMonth"),
    leakNewMoneyGapShare: required(p.baseLeakNewMoneyGapShare, "baseLeakNewMoneyGapShare"),
    lotEveDurationYears: required(p.frontLotEveDurationYears, "frontLotEveDurationYears"),
    lotBeta: required(p.frontLotBeta, "frontLotBeta"),
    operatingFloorShare: p.checkingMigration?.floorShareByProduct[productId as keyof typeof p.checkingMigration.floorShareByProduct] ?? 0,
  };
}

/** What a front lot carries in the rate-risk measures until it graduates (05.5, treasury review T16). */
export const frontLotRateRisk = (r: FrontBookRules): { readonly eveDurationYears: number; readonly beta: number } => ({ eveDurationYears: r.lotEveDurationYears, beta: r.lotBeta });

/**
 * 02.5 E2: gross new money in a month, in $. Marketing: (a ÷ 12)·Φ·(x^ε − 1), a franchise gain when positive and a smaller franchise
 * when negative. The new-money rate: (a ÷ 12)·Φ·x^ε·κ·σ·g^f ÷ notch, `rateUsd`; of it `ownBaseShare` is the bank's own base repricing (E3).
 * Written into `out` ([marketing, rate]) so the month allocates nothing.
 */
export function acquisitionUsd(r: FrontBookRules, franchiseUsd: number, marketingMultiple: number, newMoneyGapBp: number, out: Float64Array): void {
  const a0 = r.acquisitionPerMonthRate * franchiseUsd;
  const x = Math.pow(marketingMultiple, r.elasticity);
  out[0] = a0 * (x - 1);
  out[1] = (a0 * x * r.ratePerNotch * r.sensitivity * newMoneyGapBp) / r.notchBp;
}

/** 02.5 E5: the share utility's gap, the balance-weighted new-money and base gaps; a base paid above the market buys nothing (min(g^b, 0)). */
export const effectiveGapBp = (frontUsd: number, backUsd: number, frontGapBp: number, backGapBp: number): number => {
  const total = frontUsd + backUsd;
  return total === 0 ? 0 : (frontUsd * frontGapBp + backUsd * Math.min(backGapBp, 0)) / total;
};

/** 02.5 E6: the leak's gap, max(−g^b, θ·(g^f − g^b), 0) bp: how far the base is paid below the market, or the new money above the base (counted at θ, most of it already shows as migration). */
export const baseLeakGapBp = (r: FrontBookRules, frontGapBp: number, backGapBp: number): number => Math.max(-backGapBp, r.leakNewMoneyGapShare * (frontGapBp - backGapBp), 0);

/** 02.5 E6: the base leak out of `backUsd` in a month, λ_b·β_r·σ·gap ÷ 100 of the base, never more than the base less the operating floor φ·Φ (`franchiseUsd` = Φ). */
export const baseLeakUsd = (r: FrontBookRules, betaRPerPp: number, backUsd: number, franchiseUsd: number, frontGapBp: number, backGapBp: number): number =>
  Math.min(Math.max(0, backUsd - r.operatingFloorShare * franchiseUsd), (r.leakPerMonthRate * betaRPerPp * r.sensitivity * baseLeakGapBp(r, frontGapBp, backGapBp) * backUsd) / 100);
